import React, { useMemo, useCallback, useState, useEffect } from 'react';
import DateRangePicker from '../../components/DateRangePicker';
import { useAuth } from '../../hooks/useAuth';
import { shiftRequestsAPI, shiftsAPI } from '../../api/api';
import './Shifts.css';

const UpcomingShifts = ({ shifts, employees, isAdmin, searchTerm, onExchangeRequest, onDeleteShift }) => {
  const { user } = useAuth();
  const [dateRange, setDateRange] = useState({
    startDate: new Date(),
    endDate: new Date()
  });
  const [collapsedDepartments, setCollapsedDepartments] = useState({});
  const [shiftRequests, setShiftRequests] = useState([]);

  // Загружаем запросы на обмен смен
  useEffect(() => {
    fetchShiftRequests();
  }, [isAdmin]);

  const fetchShiftRequests = async () => {
    try {
      const response = isAdmin
        ? await shiftRequestsAPI.getAll()
        : await shiftRequestsAPI.getMyRequests();
      setShiftRequests(response.requests || []);
    } catch (error) {
      console.error('Ошибка загрузки запросов:', error);
      setShiftRequests([]);
    }
  };

  // Функция для получения комментария запроса по ID смены
  const getRequestCommentForShift = useCallback((shiftId) => {
    const request = shiftRequests.find(req => req.shift_id === shiftId);
    return request ? request.comment : null;
  }, [shiftRequests]);

  const formatShiftComment = useCallback((comment) => {
    if (!comment) return null;
    return comment.replace(/<[^>]*>/g, '');
  }, []);

  const handleDateRangeChange = useCallback((startDate, endDate) => {
    setDateRange({ startDate, endDate });
  }, []);

  const toggleDepartment = useCallback((department) => {
    setCollapsedDepartments(prev => ({
      ...prev,
      [department]: !prev[department]
    }));
  }, []);

  const isWorkingShift = useCallback((shift) => {
    if (!shift.description) return true;
    
    const specialTypes = [
      'Отпуск',
      'Отпуск без сохранения заработной платы', 
      'Выходной день',
      'Больничный лист',
      'День отдыха в счет дня работы в праздничный день'
    ];
    
    return !specialTypes.some(type => shift.description.includes(type));
  }, []);

  const getShiftStatus = useCallback((shift) => {
    if (shift.exchange_status) {
      return `exchange-${shift.exchange_status}`;
    }
    if (shift.comment) {
      if (shift.comment.includes('отменен')) return 'cancelled';
      if (shift.comment.includes('отклонена')) return 'rejected';
      if (shift.comment.includes('передана')) return 'transferred';
    }
    return 'normal';
  }, []);

  const getStatusDisplay = useCallback((shift) => {
    const status = getShiftStatus(shift);
    switch (status) {
      case 'exchange-pending':
        return { text: 'Запрос отправлен', type: 'pending'};
      case 'exchange-approved':
        return { text: 'Подтвержден', type: 'approved'};
      case 'exchange-rejected':
        return { text: 'Отклонен', type: 'rejected'};
      case 'exchange-cancelled':
        return { text: 'Отменен', type: 'cancelled'};
      case 'cancelled':
        return { text: 'Запрос отменен', type: 'cancelled'};
      case 'rejected':
        return { text: 'Запрос отклонен', type: 'rejected' };
      case 'transferred':
        return { text: 'Смена передана', type: 'transferred'};
      default:
        return null;
    }
  }, [getShiftStatus]);

  const canExchangeShift = useCallback((shift, user) => {
    return isAdmin || shift.user_id === user?.id;
  }, [isAdmin]);

  const isMyShift = useCallback((shift, user) => {
    return shift.user_id === user?.id;
  }, []);

  // Функция для определения типа смены и отображаемого имени
  const getShiftTypeInfo = useCallback((shift) => {
    const startTime = new Date(shift.start_time);
    const endTime = new Date(shift.end_time);
    const startHours = startTime.getHours();
    const endHours = endTime.getHours();
      
    const isDayShift = startHours === 8 && endHours === 20;
    const isDay9Shift = startHours === 9 && endHours === 18;
    const isDay9_21Shift = startHours === 9 && endHours === 21;
    const isDay11_20Shift = startHours === 11 && endHours === 20;
    const isDay11_23Shift = startHours === 11 && endHours === 23;
    const isNightShift = startHours === 20;
    
    // Определяем отображаемое имя смены
    const getShiftDisplayName = () => {
      // Если есть description в данных смены, используем его
      if (shift.description) {
        // Извлекаем только название смены из описания
        const desc = shift.description;
        if (desc.includes('Дневная смена (08:00 - 20:00)')) return 'Дневная';
        if (desc.includes('Дневная смена (09:00 - 18:00)')) return 'Дневная (09-18)';
        if (desc.includes('Дневная смена (09:00 - 21:00)')) return 'Дневная (09-21)';
        if (desc.includes('Дневная смена (11:00 - 20:00)')) return 'Дневная (11-20)';
        if (desc.includes('Дневная смена (11:00 - 23:00)')) return 'Дневная (11-23)';
        if (desc.includes('Ночная смена (20:00 - 08:00)')) return 'Ночная';
        return desc;
      }
      
      // Если description нет, определяем по часам
      if (isDayShift) return 'Дневная';
      if (isDay9Shift) return 'Дневная (09-18)';
      if (isDay9_21Shift) return 'Дневная (09-21)';
      if (isDay11_20Shift) return 'Дневная (11-20)';
      if (isDay11_23Shift) return 'Дневная (11-23)';
      if (isNightShift) return 'Ночная';
      return 'Смена';
    };
      
    return {
      isDayShift,
      isDay9Shift,
      isDay9_21Shift,
      isDay11_20Shift,
      isDay11_23Shift,
      isNightShift,
      displayName: getShiftDisplayName()
    };
  }, []);

  const shiftsByDepartment = useMemo(() => {
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);

    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);

    const dateFilteredShifts = shifts.filter(shift => {
      const shiftDate = new Date(shift.start_time);
      return shiftDate >= startDate && shiftDate <= endDate && isWorkingShift(shift);
    });

    const searchFilteredShifts = searchTerm 
      ? dateFilteredShifts.filter(shift => 
          shift.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (shift.user_position && shift.user_position.toLowerCase().includes(searchTerm.toLowerCase()))
        )
      : dateFilteredShifts;

    const grouped = {};
    
    employees.forEach(employee => {
      const department = employee.department || 'Без отдела';
      if (!grouped[department]) {
        grouped[department] = [];
      }
    });
    
    searchFilteredShifts.forEach(shift => {
      let department = 'Без отдела';
      const employee = employees.find(emp => emp.id === shift.user_id);
      if (employee) {
        department = employee.department || 'Без отдела';
      } else if (shift.user_department) {
        department = shift.user_department;
      }
      
      if (!grouped[department]) {
        grouped[department] = [];
      }
      grouped[department].push(shift);
    });

    return grouped;
  }, [dateRange, shifts, searchTerm, employees, isWorkingShift]);

  return (
    <div className="upcoming-view">
      <div className="upcoming-header">
        <label>Период:</label>
        <DateRangePicker 
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={handleDateRangeChange}
        />
      </div>

      <div className="upcoming-shifts-container">
        {Object.keys(shiftsByDepartment).length > 0 ? (
          Object.entries(shiftsByDepartment).map(([department, departmentShifts]) => (
            <div key={department} className="department-group">
              <div 
                className="department-header"
                onClick={() => toggleDepartment(department)}
              >
                <i className="fas fa-building"></i>
                <h4>{department}</h4>
                <span className="shift-count">{departmentShifts.length} смен</span>
                <i className={`fas fa-chevron-down department-toggle ${collapsedDepartments[department] ? 'collapsed' : ''}`}></i>
              </div>
              <div 
                className={`department-shifts ${collapsedDepartments[department] ? 'collapsed' : ''}`}
              >
                {departmentShifts.length > 0 ? (
                  departmentShifts.map(shift => {
                    const startTime = new Date(shift.start_time);
                    const shiftTypeInfo = getShiftTypeInfo(shift);
                    const statusDisplay = getStatusDisplay(shift);
                    const canExchange = canExchangeShift(shift, user);
                    const isMy = isMyShift(shift, user);
                    const requestComment = getRequestCommentForShift(shift.id);
                    
                    return (
                      <div key={shift.id} className={`shift-card ${statusDisplay ? `shift-${statusDisplay.type}` : ''}`}>
                        <div className="shift-info">
                          <div className="shift-main">
                            <div className="shift-date">
                              <strong>{startTime.toLocaleDateString('ru-RU')}</strong>
                              <span className={`shift-type-badge ${
                                  shiftTypeInfo.isDayShift ? 'day-shift' : 
                                  shiftTypeInfo.isDay9Shift ? 'day8-shift' : 
                                  shiftTypeInfo.isDay9_21Shift ? 'day9-21-shift' :
                                  shiftTypeInfo.isDay11_20Shift ? 'day11-20-shift' :
                                  shiftTypeInfo.isDay11_23Shift ? 'day11-23-shift' :
                                  shiftTypeInfo.isNightShift ? 'night-shift' :
                                  'other-shift'
                                }`}>
                                {shiftTypeInfo.displayName}
                              </span>
                            </div>
                            
                            <div className="employee-info">
                              <div className="employee-name">
                                {shift.user_name}
                                {isMy && (
                                  <span className="my-shift-badge">Моя смена</span>
                                )}
                              </div>
                              {shift.user_position && (
                                <div className="employee-position">{shift.user_position}</div>
                              )}
                            </div>
                          
                          {statusDisplay && (
                            <div className="shift-status">
                              <span className={`status-badge status-${statusDisplay.type}`}>
                                <i className={`fas ${
                                  statusDisplay.type === 'pending' ? 'fa-clock' :
                                  statusDisplay.type === 'approved' ? 'fa-check' :
                                  statusDisplay.type === 'rejected' ? 'fa-times' :
                                  statusDisplay.type === 'cancelled' ? 'fa-ban' :
                                  'fa-exchange-alt'
                                }`}></i>
                                {statusDisplay.text}
                              </span>
                            </div>
                          )}
                          </div>

                          {/* Блок истории передачи смены из request.comment */}
                          {requestComment && (
                            <div className="shift-comment-section">
                              <div className="shift-comment-header">
                                <i className="fas fa-exchange-alt"></i>
                                <strong>История передачи:</strong>
                              </div>
                              <div className="shift-comment-content">
                                {formatShiftComment(requestComment)}
                              </div>
                            </div>
                          )}

                          {/* Обычный комментарий смены */}
                          {shift.comment && (
                            <div className="shift-comment">
                              <i className="fas fa-comment"></i>
                              <span>{formatShiftComment(shift.comment)}</span>
                            </div>
                          )}
                        </div>

                        <div className="shift-actions">
                          {(isAdmin || isMy) && (
                            <button 
                              className="exchange-btn"
                              onClick={() => onExchangeRequest(shift)}
                              disabled={statusDisplay}
                              title={
                                statusDisplay ? statusDisplay.text : "Запрос на обмен"
                              }
                            >
                              <i className="fas fa-exchange-alt"></i>
                              {statusDisplay ? statusDisplay.text : 'Обмен'}
                            </button>
                          )}
                          {isAdmin && (
                            <button 
                              className="btn-delete"
                              onClick={() => onDeleteShift(shift.id)}
                              title="Удалить смену"
                            >
                              <i className="fas fa-trash"></i>
                              Удалить смену
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="no-shifts-in-department">
                    <i className="fas fa-calendar-times"></i>
                    <span>В этом отделе нет смен в выбранном периоде</span>
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="no-shifts">
            <i className="fas fa-calendar-times"></i>
            <h3>Нет смен в выбранном диапазоне</h3>
            <p>Попробуйте изменить даты или очистить поиск</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpcomingShifts;
